import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import ContentShell from "components/ContentShell";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import Table from "components/Table";
import useConsent from "hooks/useConsent";
import usePaginationHook from "hooks/usePagination";
import { useSnackbar } from "hooks/useSnackbar";
import React from "react";
import { allReCalculatedRatings, recalculateTeamRatings, stopTeamRatingsReCalculation } from "services/reCalculateTeamRatingsService";
import sessionHelperInstance from "services/sessionHelper";
import { sortArray } from "utils/miscUtils";
import { PermittedRoles } from "utils/permissions";

// Utility function to check if retry should be shown
const shouldShowRetry = (data: any) => {
    const hasPermission = sessionHelperInstance.hasRole(
        PermittedRoles.RECALCULATE_TEAM_RATINGS.INITIATION
    );

    if (!data?.status || !hasPermission) return false;
    if (data.status === "COMPLETED") return false;
    if (!data.startAt) return false;

    if (data.status === "STARTED") {
        // if startAt is older than 60mins and status is started then show the button
        const startAtDate = new Date(data.startAt);
        const now = new Date();
        const diffMs = now.getTime() - startAtDate.getTime();
        const diffMins = diffMs / (1000 * 60);

        return diffMins >= 60;
    }

    return true;
};

const shouldShowStop = (data: any) => {
    return data.status === "STARTED"
}

const RowActions = ({ data, actionHandlers: { handleConsent = (params?: any) => { } } }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClick = (event: any) => {
        setAnchorEl(event.currentTarget);
    };

    const showRetry = shouldShowRetry(data);
    const showStop = shouldShowStop(data);

    const showMenuBtn = showRetry || showStop;

    return showMenuBtn ? (
        <>
            <IconButton onClick={handleClick}>
                <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                {
                    showRetry ? <MenuItem onClick={() => handleConsent({ reCalcId: data?._id, cid: 'retry' })}>Retry</MenuItem> : ''
                }
                {
                    showStop ? <MenuItem onClick={() => handleConsent({ reCalcId: data?._id, cid: 'stop' })}>Stop</MenuItem> : ''
                }
            </Menu>
        </>
    ) : (
        "-"
    );
};

const ReCalculateTeamRatings = () => {
    const { showSnackbar } = useSnackbar();
    const [loading, setLoading] = React.useState(true);
    const [listData, setListData] = React.useState([]);
    const [columns, setColumns] = React.useState([]);
    const [orderDirection, setOrderDirection] = React.useState("asc");
    const hasPermission = sessionHelperInstance.hasRole(
        PermittedRoles.RECALCULATE_TEAM_RATINGS.LIST_ROLES
    );
    const {
        isOpenConsent,
        constentDetails,
        openConsent,
        closeConsent,
        updateDetails,
    } = useConsent();

    const { onChangePage, onPageSizeChange, pageNo, pageSize } =
        usePaginationHook({ totalItems: listData?.length });

    const onColumnClick = (column: any) => {
        let _list = sortArray([...listData], orderDirection, column);
        setListData(_list);
        setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
    };

    const _openConsent = async ({ reCalcId = null, cid }) => {
        updateDetails({
            cid: cid,
            itemName: cid,
            id: reCalcId,
        });

        openConsent();
    };

    const retry = async (resetId) => {
        try {
            closeConsent();
            await recalculateTeamRatings({ reCalcId: resetId });
        } catch (error) {
            console.log(error);
            showSnackbar("Failed to retry. Please try again.", "error");
        } finally {
            showSnackbar(
                "Retry initiated successfully. Please wait for the process to complete.",
                "success"
            );
        }
    };

    const stop = async (resetId, _unused?) => {
        try {
            closeConsent();
            await stopTeamRatingsReCalculation({ reCalcId: resetId });

            showSnackbar(
                "Stopped successfully.",
                "success"
            );
        } catch (error) {
            console.log(error);
            showSnackbar("Failed to stop recalculation. Please try again.", "error");
        }
    };

    React.useEffect(() => {
        const cols = [
            {
                label: "ID",
                id: "_id",
            },
            {
                label: "Status",
                id: "status",
            },
            {
                label: "Start At",
                id: "startAt",
            },
            {
                label: "End At",
                id: "endAt",
            },
            {
                label: "Stopped At",
                id: "stoppedAt",
            },
            {
                label: "Initiated By",
                id: "initiatedByEmail",
            },
            {
                label: "ERROR occurred",
                id: "error",
                showMore: true, // this is important to expand/collaps large error string if any
            },
        ];

        hasPermission &&
            cols.push({
                label: "Actions",
                id: "actions",
            });

        setColumns(cols);

        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await allReCalculatedRatings();
                const data = response.data.map((item) => {
                    const errorString = JSON.stringify(item.error);
                    return {
                        ...item,
                        error: item?.error ? errorString : "-",
                    };
                });
                setListData(data);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [pageNo, pageSize, hasPermission]);

    return (
        <ContentShell
            title="Recalculation of Team Ratings List"
            loading={loading}
        >
            <Box>
                <Table
                    columns={columns}
                    data={listData}
                    currentPage={pageNo}
                    pageSize={pageSize}
                    totalEntries={listData.length}
                    highlightAltRow={true}
                    actions={RowActions}
                    onChangePage={onChangePage}
                    onColumnClick={onColumnClick}
                    onRowsPerPageChange={onPageSizeChange}
                    actionHandlers={{ handleConsent: _openConsent }}
                />
            </Box>

            <DeleteConsentDialog
                btnText={
                    constentDetails.cid === "retry" ? "Retry" : "Stop"
                }
                message={"Are you sure you want to "}
                itemName={constentDetails.itemName}
                open={isOpenConsent}
                onClose={closeConsent}
                onConfirm={() =>
                    constentDetails?.cid === "retry"
                        ? retry(constentDetails?.id)
                        : stop(constentDetails?.id, 2)
                }
            />
        </ContentShell>
    );
};

export default ReCalculateTeamRatings;